import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { getPresetTheme, buildNexaStayTheme, DEFAULT_ACCENT, THEME_PRESET_KEYS } from './nexastayTheme';
import { normalizeHex } from './nexastayTheme';

const STORAGE_KEY = 'nexastay_theme_preset';

/** Normalize preset key: 'gradient' was removed, treat as 'light'. */
function normalizePresetKey(key) {
  if (!key || typeof key !== 'string') return 'dark';
  const k = key.trim().toLowerCase();
  return k === 'gradient' ? 'light' : k;
}

const NexaStayThemeContext = createContext(null);

export function NexaStayThemeProvider({ hotel, children }) {
  const [themePreset, setThemePresetState] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = window.localStorage.getItem(STORAGE_KEY) || 'dark';
    return THEME_PRESET_KEYS.includes(stored) ? stored : normalizePresetKey(stored) || 'dark';
  });

  // Sync preset from hotel (set from console.roomnexa.com) so <slug>.roomnexa.com shows same theme
  useEffect(() => {
    const fromHotel = hotel?.nexastay_theme_preset && THEME_PRESET_KEYS.includes(String(hotel.nexastay_theme_preset).toLowerCase())
      ? String(hotel.nexastay_theme_preset).toLowerCase()
      : null;
    if (fromHotel && fromHotel !== themePreset) {
      setThemePresetState(fromHotel);
    }
  }, [hotel?.nexastay_theme_preset]);

  useEffect(() => {
    if (typeof window !== 'undefined' && THEME_PRESET_KEYS.includes(themePreset)) {
      window.localStorage.setItem(STORAGE_KEY, themePreset);
    }
  }, [themePreset]);

  const setThemePreset = (key) => {
    const normalized = THEME_PRESET_KEYS.includes(key) ? key : normalizePresetKey(key);
    if (THEME_PRESET_KEYS.includes(normalized)) setThemePresetState(normalized);
  };

  const theme = useMemo(() => {
    const presetKey = THEME_PRESET_KEYS.includes(themePreset) ? themePreset : (normalizePresetKey(themePreset) || 'dark');
    const accentOverride = hotel ? (normalizeHex(hotel.theme_color) || undefined) : undefined;
    return getPresetTheme(presetKey, accentOverride);
  }, [themePreset, hotel, hotel?.theme_color]);

  const value = useMemo(
    () => ({ theme, themePreset, setThemePreset, hotel }),
    [theme, themePreset, hotel]
  );

  return (
    <NexaStayThemeContext.Provider value={value}>
      {children}
    </NexaStayThemeContext.Provider>
  );
}

export function useNexaStayTheme() {
  const ctx = useContext(NexaStayThemeContext);
  if (!ctx) {
    return buildNexaStayTheme(DEFAULT_ACCENT);
  }
  return ctx.theme;
}

export function useNexaStayThemeContext() {
  const ctx = useContext(NexaStayThemeContext);
  return ctx || {
    theme: buildNexaStayTheme(DEFAULT_ACCENT),
    themePreset: 'dark',
    setThemePreset: () => {},
    hotel: null,
  };
}
